import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ContactSupportIcon from '@mui/icons-material/ContactSupport'
import FavoriteIcon from '@mui/icons-material/Favorite'
import LayersIcon from '@mui/icons-material/Layers'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import TranslateIcon from '@mui/icons-material/Translate'
import { Box, ListItemButton, Select, Typography, useColorScheme } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import type { ReactNode } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useLocalStorage } from '@uidotdev/usehooks'
import { useTranslation } from 'react-i18next'
import AutomaticSheet from 'src/components/sheet/AutomaticSheet'
import SheetHeader from 'src/components/sheet/SheetHeader'

declare const __BREZAVTA_VERSION__: string
export const Route = createFileRoute('/settings')({
    component: Settings,
})

function Settings() {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [language, setLanguage] = useLocalStorage('userLanguage')
    const [timeDisplayPreference, setTimeDisplayPreference] = useLocalStorage<'relative' | 'absolute'>('timeDisplayPreference', 'relative')
    const commitHash = __BREZAVTA_VERSION__.substring(0, 8)
    const { mode, setMode } = useColorScheme()
    return (
        <AutomaticSheet>
            <title>{t('Settings')}</title>
            <SheetHeader heading={t('Settings')} share={false} />
            <Box>

                <ListItemButton onClick={() => navigate({ to: '/filter' })}>
                    <LayersIcon sx={{ marginRight: 1, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="body1" color="textprimary">{t('Filters')}</Typography>
                        <Typography variant="body2" color="textSecondary">{t('Visible modes of transport on map')}</Typography>
                    </Box>
                    <NavigateNextIcon sx={{ marginLeft: 'auto', color: 'primary.main' }} />
                </ListItemButton>

                <ListItemButton onClick={() => navigate({ to: '/settings/pois' })}>
                    <FavoriteIcon sx={{ marginRight: 1, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="body1" color="textprimary">{t('Favourites')}</Typography>
                        <Typography variant="body2" color="textSecondary">{t('See and delete your favourite locations')}</Typography>
                    </Box>
                    <NavigateNextIcon sx={{ marginLeft: 'auto', color: 'primary.main' }} />
                </ListItemButton>

                <SettingsItem
                    name={t('Language')}
                    icon={<TranslateIcon sx={{ color: 'primary.main' }} />}
                    control={(
                        <Select
                            size="small"
                            value={language}
                            onChange={(event) => {
                                setLanguage(event.target.value)
                            }}
                        >
                            <MenuItem value="en">{t('English')}</MenuItem>
                            <MenuItem value="sl">{t('Slovenščina')}</MenuItem>
                        </Select>
                    )}
                />
                <SettingsItem
                    name={t('Theme')}
                    icon={<LayersIcon sx={{ color: 'primary.main' }} />}
                    control={(
                        <Select
                            size="small"
                            value={mode}
                            onChange={(event) => {
                                setMode(event.target.value)
                            }}
                        >
                            <MenuItem value="system">{t('theme_system')}</MenuItem>
                            <MenuItem value="light">{t('theme_light')}</MenuItem>
                            <MenuItem value="dark">{t('theme_dark')}</MenuItem>
                        </Select>
                    )}
                />
                <SettingsItem
                    name={t('Time display')}
                    icon={<AccessTimeIcon sx={{ color: 'primary.main' }} />}
                    control={(
                        <Select
                            size="small"
                            value={timeDisplayPreference}
                            onChange={(event) => {
                                setTimeDisplayPreference(event.target.value as 'relative' | 'absolute')
                            }}
                        >
                            <MenuItem value="relative">{t('time_display_relative_verbose')}</MenuItem>
                            <MenuItem value="absolute">{t('time_display_absolute_verbose')}</MenuItem>
                        </Select>
                    )}
                />
                <ListItemButton>
                    <ContactSupportIcon sx={{ marginRight: 1, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="body1" color="textprimary">{t('Contact')}</Typography>
                        <Typography variant="body2" color="textSecondary">
                            {t('Get in touch with us at ')}
                            {' '}
                            <a href="mailto:brezavta@derp.si" style={{ textDecoration: 'underline' }}>brezavta@derp.si</a>
                        </Typography>
                    </Box>
                </ListItemButton>
                <SettingsItem
                    name={t('Version')}
                    description={t('Please include this in any bug reports.')}
                    icon={<LayersIcon sx={{ color: 'primary.main' }} />}
                    control={<Typography variant="body2" color="textSecondary">{commitHash}</Typography>}
                />
            </Box>
        </AutomaticSheet>
    )
}

const SettingsItem = ({ name, icon, description, control }: { name: ReactNode, icon: ReactNode, description?: ReactNode, control: ReactNode }) => {
    return (
        <Box display="flex" flexDirection="row" alignItems="center" sx={{ width: '100%', height: '3rem', p: 2 }}>
            {icon}
            <Box sx={{ flex: 1, p: 1 }}>
                <Typography variant="body1">{name}</Typography>
                <Typography variant="body2" color="textSecondary">{description}</Typography>
            </Box>
            {control}
        </Box>
    )
}
